import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { rpcClient, orpc } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';

interface BulkSnoozeRerunResult {
  invoiceId: string;
  success: boolean;
  error?: string;
}

interface BulkSnoozeRerunState {
  isLoading: boolean;
  results: BulkSnoozeRerunResult[];
}

export function useBulkSnoozeRerunAPInvoices() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [state, setState] = useState<BulkSnoozeRerunState>({
    isLoading: false,
    results: [],
  });

  const bulkSnoozeRerun = useCallback(
    async (invoiceIds: string[], scheduledAt: Date) => {
      if (invoiceIds.length === 0) return [];

      setState({ isLoading: true, results: [] });

      try {
        const response = await rpcClient.workflows.snoozeRerunInvoices({
          invoiceIds,
          scheduledAt,
        });

        const results = response.results;
        const successCount = results.filter((r) => r.success).length;
        const failureCount = results.filter((r) => !r.success).length;

        setState({ isLoading: false, results });

        if (failureCount === 0) {
          showToast({
            title: 'Rerun Scheduled',
            message: `Successfully scheduled ${successCount} invoice${successCount !== 1 ? 's' : ''} for rerun`,
            color: 'success',
            icon: 'check',
          });
        } else if (successCount === 0) {
          showToast({
            title: 'Schedule Failed',
            message: `Failed to schedule all ${failureCount} invoice${failureCount !== 1 ? 's' : ''}`,
            color: 'error',
            icon: 'error',
          });
        } else {
          showToast({
            title: 'Schedule Partially Complete',
            message: `Scheduled ${successCount} of ${results.length} invoices. ${failureCount} failed.`,
            color: 'warning',
            icon: 'warning',
          });
        }

        queryClient.invalidateQueries({
          queryKey: orpc.accountsPayable.invoices.list.queryKey({ input: {} }),
        });

        return results;
      } catch (error) {
        setState({ isLoading: false, results: [] });
        showToast({
          title: 'Schedule Failed',
          message:
            error instanceof Error ? error.message : 'Unknown error occurred',
          color: 'error',
          icon: 'error',
        });
        return [];
      }
    },
    [queryClient, showToast],
  );

  const reset = useCallback(() => {
    setState({ isLoading: false, results: [] });
  }, []);

  const successCount = state.results.filter((r) => r.success).length;
  const failureCount = state.results.filter((r) => !r.success).length;

  return {
    bulkSnoozeRerun,
    reset,
    isLoading: state.isLoading,
    results: state.results,
    successCount,
    failureCount,
  };
}
